import { Hourglass, Mail, MessageCircle, OctagonAlert } from "lucide-react";
import type { Organization } from "../api/types";
import { useT } from "../i18n/core";
import { CONTACT, whatsappHref } from "../lib/contact";
import { useFormat } from "../lib/format";
import { trialBannerMessages } from "./trialBanner.i18n";

const DAY = 86_400_000;

/** Dias inteiros que faltam até o fim, arredondando para cima (resta meio dia: 1). */
export function trialDaysLeft(endsAt: string, now = Date.now()): number {
  return Math.ceil((new Date(endsAt).getTime() - now) / DAY);
}

/**
 * Faixa do teste gratuito (ADR 0038) no topo do conteúdo. Durante o teste,
 * mostra quantos dias faltam; depois do fim, avisa com força que os backups
 * pararam e que restaurar continua liberado por um tempo. now é a hora em
 * que a lista de organizações chegou, para a faixa não mudar a cada render.
 */
export function TrialBanner({ org, now }: { org: Organization | undefined; now: number }) {
  const t = useT(trialBannerMessages);
  const f = useFormat();
  if (!org?.trialEndsAt || org.status === "closed") return null;

  const ended = new Date(org.trialEndsAt).getTime() <= now;
  const date = f.fullDate(org.trialEndsAt);
  const whatsapp = (
    <a className="btn btn-sm" href={whatsappHref(t.whatsappText(org.name))} target="_blank" rel="noopener noreferrer">
      <MessageCircle aria-hidden="true" /> {t.whatsapp}
    </a>
  );

  if (ended) {
    return (
      <div className="callout crit" role="alert" style={{ flexWrap: "wrap", alignItems: "center" }}>
        <OctagonAlert aria-hidden="true" />
        <div style={{ display: "grid", gap: 2, flex: "1 1 280px" }}>
          <strong>{t.endedTitle(date)}</strong>
          <span>{t.endedDetail(org.trialRestoreUntil ? f.fullDate(org.trialRestoreUntil) : null)}</span>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {whatsapp}
          <a className="btn btn-sm btn-ghost" href={`mailto:${CONTACT.mail}`}>
            <Mail aria-hidden="true" /> {t.mail(CONTACT.mail)}
          </a>
        </div>
      </div>
    );
  }

  const left = trialDaysLeft(org.trialEndsAt, now);
  return (
    <div className={left <= 7 ? "callout warn" : "callout"} role="status" style={{ flexWrap: "wrap", alignItems: "center" }}>
      <Hourglass aria-hidden="true" />
      <div style={{ display: "grid", gap: 2, flex: "1 1 280px" }}>
        <strong>{t.remaining(left)}</strong>
        <span>{t.activeDetail(date)}</span>
      </div>
      {whatsapp}
    </div>
  );
}
